import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/api/client';
import { copilotChatStream } from '@/api/stream';
import { Button } from '@/components/ui/Button';
import { StateMessage } from '@/components/StateMessage/StateMessage';
import styles from './copilot.module.css';
import { ContextPanel } from './ContextPanel';
import { PlanPanel } from './PlanPanel';
import { RunTimeline } from './RunTimeline';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  source_ids?: string[];
};

export function CopilotWorkspace({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [timelineSteps, setTimelineSteps] = useState<string[]>([]);
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const streamingRef = useRef(false);

  useEffect(() => {
    streamingRef.current = streaming;
  }, [streaming]);

  const sessionsQ = useQuery({
    queryKey: ['copilot-sessions', projectId],
    queryFn: () =>
      apiFetch<{
        sessions: { id: string; title: string | null; updated_at: string }[];
      }>(`/api/copilot/sessions?project_id=${projectId}`),
  });

  const messagesQ = useQuery({
    queryKey: ['copilot-messages', sessionId],
    enabled: Boolean(sessionId),
    queryFn: () =>
      apiFetch<{
        messages: {
          id: string;
          role: string;
          content: string;
          source_ids?: string[];
        }[];
      }>(`/api/copilot/sessions/${sessionId}/messages`),
  });

  useEffect(() => {
    if (streamingRef.current || !messagesQ.data?.messages) return;
    setLocalMessages(
      messagesQ.data.messages.map((m) => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        content: m.content,
        source_ids: Array.isArray(m.source_ids) ? (m.source_ids as string[]) : [],
      })),
    );
  }, [messagesQ.data, sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages, streaming, toolStatus]);

  const newSession = useMutation({
    mutationFn: () =>
      apiFetch<{ session: { id: string } }>('/api/copilot/sessions', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId }),
      }),
    onSuccess: (data) => {
      setSessionId(data.session.id);
      setLocalMessages([]);
      void qc.invalidateQueries({ queryKey: ['copilot-sessions', projectId] });
    },
  });

  const send = async () => {
    const text = draft.trim();
    if (!text || streaming) return;
    setDraft('');
    setStreaming(true);
    setToolStatus(null);
    setTimelineSteps([]);

    const userMsg: ChatMessage = {
      id: `local-u-${Date.now()}`,
      role: 'user',
      content: text,
    };
    const assistantId = `local-a-${Date.now()}`;
    setLocalMessages((prev) => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '' }]);

    let sources: string[] = [];
    let gotToken = false;
    let streamError: string | null = null;

    try {
      await copilotChatStream(
        { project_id: projectId, session_id: sessionId ?? undefined, message: text },
        {
          onMeta: (meta) => {
            setSessionId(meta.session_id);
            sources = meta.source_ids;
          },
          onTool: (ev) => {
            if (ev.status === 'start') setToolStatus(`Data ophalen: ${ev.name}…`);
            if (ev.status === 'done') {
              setToolStatus(ev.error ? `Tool ${ev.name}: ${ev.error}` : 'Antwoord formuleren…');
            }
          },
          onToken: (token) => {
            gotToken = true;
            setToolStatus(null);
            setLocalMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: m.content + token, source_ids: sources } : m,
              ),
            );
          },
          onTimeline: (data) => {
            setTimelineSteps((prev) => [...prev, data.step]);
          },
          onDone: (done) => {
            setSessionId(done.session_id);
            if (done.content) {
              setLocalMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? {
                        ...m,
                        content: done.content ?? m.content,
                        source_ids: done.source_ids,
                      }
                    : m,
                ),
              );
            }
            void qc.invalidateQueries({ queryKey: ['copilot-sessions', projectId] });
            void qc.invalidateQueries({ queryKey: ['copilot-runs', done.session_id] });
            void qc.invalidateQueries({ queryKey: ['copilot-plan', done.session_id] });
          },
          onError: (err) => {
            streamError = err.message;
          },
          onStatus: (st) => {
            if (st.phase === 'tools') setToolStatus('Projectdata ophalen…');
            if (st.phase === 'answer') setToolStatus('Antwoord schrijven…');
            if (st.phase === 'fallback') setToolStatus('Tools niet beschikbaar — FO-only antwoord…');
          },
        },
      );

      if (!gotToken && !streamError) {
        streamError = 'Geen antwoord ontvangen (controleer LLM-instellingen of probeer opnieuw).';
      }
      if (streamError) {
        setLocalMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: m.content.trim() || `Fout: ${streamError}` }
              : m,
          ),
        );
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Verbinding mislukt';
      setLocalMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, content: `Fout: ${msg}` } : m)),
      );
    } finally {
      setStreaming(false);
      setToolStatus(null);
      if (sessionId) {
        void qc.invalidateQueries({ queryKey: ['copilot-messages', sessionId] });
      }
    }
  };

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <Button type="button" onClick={() => newSession.mutate()} disabled={newSession.isPending}>
          Nieuw gesprek
        </Button>
        <ul className={styles.sessionList}>
          {(sessionsQ.data?.sessions ?? []).map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className={sessionId === s.id ? styles.sessionActive : styles.sessionBtn}
                onClick={() => setSessionId(s.id)}
              >
                {s.title?.trim() || 'Gesprek'}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className={styles.chat}>
        {!sessionId && !localMessages.length ? (
          <StateMessage title="Assistent — platform Q&A">
            Vraag over metrics, campagnes, doelgroepen of integraties. De assistent gebruikt FO-docs,
            capability registry en read-only projectdata.
          </StateMessage>
        ) : null}

        <div className={styles.messages}>
          {localMessages.map((m) => {
            const body = m.content || (streaming && m.role === 'assistant' ? '…' : '');
            const isEmptyAssistant = m.role === 'assistant' && !body.trim();
            return (
              <div
                key={m.id}
                className={m.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant}
              >
                <div className={styles.bubbleRole}>{m.role === 'user' ? 'Jij' : 'Assistent'}</div>
                <div className={`${styles.bubbleBody} ${isEmptyAssistant ? styles.bubbleEmpty : ''}`}>
                  {isEmptyAssistant ? 'Geen antwoord — stuur opnieuw of controleer LLM-gateway.' : body}
                </div>
                {m.role === 'assistant' && m.source_ids?.length ? (
                  <div className={styles.sources}>
                    Bronnen: {m.source_ids.map((id) => `[${id}]`).join(' ')}
                  </div>
                ) : null}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {toolStatus ? <p className={styles.toolStatus}>{toolStatus}</p> : null}
        {timelineSteps.length ? (
          <ul className={styles.timelineList} aria-label="Stappen">
            {timelineSteps.map((step, i) => (
              <li key={`${step}-${i}`}>{step}</li>
            ))}
          </ul>
        ) : null}

        <form
          className={styles.composer}
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            className={styles.input}
            rows={3}
            placeholder="Vraag over Marketing Hub…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={streaming}
          />
          <Button
            type="submit"
            className={styles.sendBtn}
            disabled={streaming || !draft.trim()}
          >
            {streaming ? 'Bezig…' : 'Verstuur'}
          </Button>
        </form>
      </div>

      <div className={styles.contextColumn}>
        <ContextPanel projectId={projectId} />
        <PlanPanel sessionId={sessionId} projectId={projectId} />
        <RunTimeline sessionId={sessionId} />
      </div>
    </div>
  );
}
